import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Switch,
    FormControlLabel,
    IconButton,
    Divider,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Snackbar,
    Alert
} from "@mui/material";

import { useParams, useNavigate } from "react-router-dom";

import { rtdb, storage } from "../../services/firebase";
import { ref, get, set, update } from "firebase/database";
import {
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from "firebase/storage";


import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import { getProductCategorias } from "../../utils/productCategories";

const ACCEPTED_IMAGE_MIME = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);

function isAcceptedImageFile(file) {
    if (!file || !(file instanceof File)) return false;
    if (ACCEPTED_IMAGE_MIME.has(file.type)) return true;
    if (!file.type) {
        return /\.(jpe?g|png|gif|webp)$/i.test(file.name || "");
    }
    return false;
}

export default function EditProduct() {

    const { id } = useParams();

    const navigate = useNavigate();

    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [nome, setNome] = useState("");
    const [descricao, setDescricao] = useState("");
    const [marca, setMarca] = useState("");
    const [categorias, setCategorias] = useState([]);
    const [preco, setPreco] = useState("");
    const [isPromo, setIsPromo] = useState(false);
    const [precoPromo, setPrecoPromo] = useState("");
    const [precoDiscount, setPrecoDiscount] = useState("");
    const [variacoes, setVariacoes] = useState([{ nome: "", quantidade: "" }]);
    const [specs, setSpecs] = useState([{ key: "", value: "" }]);

    const [imagens, setImagens] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const dragCounterRef = useRef(0);
    const fileInputRef = useRef(null);

    const [estoqueOpen, setEstoqueOpen] = useState(false);

    const [status, setStatus] = useState("active");

    const isFormInvalid = !nome || !marca || categorias.length === 0 || imagens.length === 0;

    const [feedback, setFeedback] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    const showFeedback = (message, severity = "success") => {
        setFeedback({
            open: true,
            message,
            severity
        });
    };

    useEffect(() => {
        carregarMarcasECategorias();
    }, []);

    useEffect(() => {
        if (!loadingData) {
            loadProduct();
        }
    }, [id, loadingData]);

    async function carregarMarcasECategorias() {
        try {
            const appSnap = await get(ref(rtdb, "app"));
            if (appSnap.exists()) {
                const data = appSnap.val();
                setBrands(Array.isArray(data.brands) ? data.brands.map((nome) => ({ id: nome.toLowerCase().replace(/\s+/g, "_"), nome })) : []);
                setCategories(Array.isArray(data.category) ? data.category.map((nome) => ({ id: nome.toLowerCase().replace(/\s+/g, "_"), nome })) : []);
            }
        } catch (err) {
            console.error("Erro ao carregar marcas e categorias:", err);
        } finally {
            setLoadingData(false);
        }
    }

    async function loadProduct() {
        setLoading(true);
        try {
            const snap = await get(ref(rtdb, `products/${id}`));

            if (snap.exists()) {
                const p = snap.val();

                setNome(p.nome);
                setDescricao(p.descricao);
                setMarca(p.marca);
                setCategorias(getProductCategorias(p));
                setPreco(p.precos?.normal || "");
                setIsPromo(p.precos?.isPromo || false);
                setPrecoPromo(p.precos?.promo || "");
                setPrecoDiscount(p.precos?.discount || "");
                setVariacoes(p.variacoes || []);
                setSpecs(p.specs || []);

                setStatus(p.status || "active");

                // imagens estruturadas
                setImagens(
                    (p.imagens || []).map((url) => ({
                        id: crypto.randomUUID(),
                        url,
                        isNew: false
                    }))
                );
            }
        } catch (err) {
            console.error("Erro ao carregar produto:", err);
        } finally {
            setLoading(false);
        }
    }

    async function handleSave() {

        if (imagens.length < 1 || imagens.length > 5) {
            showFeedback("O produto deve ter entre 1 e 5 imagens.", "warning");
            return;
        }

        setSaving(true);

        try {
            const imageUrls = await processImages();

            const updated = {
                id,
                nome,
                descricao,
                status,
                marca,
                categorias,
                categoria: categorias[0] || "",
                variacoes,
                specs,
                imagens: imageUrls, // ORDEM PRESERVADA
                precos: {
                    normal: preco,
                    isPromo,
                    promo: isPromo ? precoPromo : null,
                    discount: precoDiscount ? precoDiscount : null
                }
            };

            await update(ref(rtdb, `products/${id}`), updated);

            showFeedback("Produto atualizado com sucesso!", "success");


        } catch (err) {
            console.error(err);
            showFeedback("Erro ao salvar produto. Tente novamente.", "error");
        }

        setSaving(false);

    }

    function addImageFiles(rawFiles) {
        const all = Array.from(rawFiles || []).filter((f) => f instanceof File);
        const accepted = all.filter(isAcceptedImageFile);

        if (accepted.length === 0) {
            showFeedback("Nenhuma imagem válida. Use JPG, PNG, WEBP ou GIF.", "warning");
            return;
        }

        setImagens((prev) => {
            const room = Math.max(0, 5 - prev.length);
            if (room === 0) {
                setTimeout(() => showFeedback("Máximo de 5 imagens.", "warning"), 0);
                return prev;
            }
            const toAdd = accepted.slice(0, room);
            if (accepted.length > room) {
                setTimeout(
                    () => showFeedback(`Só foi possível adicionar ${room} imagem(ns) (máximo 5).`, "warning"),
                    0
                );
            } else if (all.length > accepted.length) {
                setTimeout(
                    () => showFeedback("Alguns arquivos foram ignorados (formato não suportado).", "warning"),
                    0
                );
            }
            const novas = toAdd.map((file) => ({
                id: crypto.randomUUID(),
                url: URL.createObjectURL(file),
                file,
                isNew: true,
            }));
            return [...prev, ...novas];
        });
    }

    function handleImages(e) {
        addImageFiles(e.target.files);
        e.target.value = "";
    }

    function handleDragEnter(e) {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (e.dataTransfer.types && e.dataTransfer.types.includes("Files")) {
            setDragActive(true);
        }
    }

    function handleDragLeave(e) {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            dragCounterRef.current = 0;
            setDragActive(false);
        }
    }

    function handleDragOver(e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types && e.dataTransfer.types.includes("Files")) {
            e.dataTransfer.dropEffect = "copy";
        }
    }

    function handleDrop(e) {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current = 0;
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addImageFiles(e.dataTransfer.files);
        }
    }

    function openFilePicker() {
        fileInputRef.current?.click();
    }

    function removeImage(imageId) {
        setImagens(imagens.filter(img => img.id !== imageId));
    }

    async function processImages() {
        const finalUrls = [];

        for (const img of imagens) {
            if (!img.isNew) {
                finalUrls.push(img.url);
                continue;
            }

            const imageRef = storageRef(
                storage,
                `products/${id}/${img.id}`
            );

            await uploadBytes(imageRef, img.file);
            const downloadURL = await getDownloadURL(imageRef);

            finalUrls.push(downloadURL);
        }

        return finalUrls;
    }

    const formatMoney = (value) => {
        if (!value) return "";
        value = value.replace(/\D/g, "");
        value = (value / 100).toFixed(2).replace(".", ",");
        return "R$ " + value.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    const labelStyles = {
        "& .MuiInputLabel-root": { color: "#777", fontWeight: 500 },
        "& .MuiInputLabel-root.Mui-focused": { color: "#0A2A79" },
        "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#0A2A79" }
    };

    if (loading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                <Typography>Carregando...</Typography>
            </Box>
        );

    return (
        <Box sx={{ mt: { xs: 2, md: 4 }, pr: { xs: 0, md: 4 }, width: "100%", maxWidth: "100%", overflow: "auto" }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: "1.35rem", md: "1.5rem" } }}>
                Editar Produto — {id}
            </Typography>

            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 3 }}>

                <Grid container spacing={3} alignItems="center">

                    {/* ID */}
                    <Grid item xs={12} md={3}>
                        <TextField label="ID" value={id} fullWidth disabled />
                    </Grid>

                    {/* Nome */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Nome do Produto"
                            fullWidth
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            sx={labelStyles}
                        />
                    </Grid>

                    {/* Marca */}
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth sx={labelStyles}>
                            <InputLabel>Marca</InputLabel>
                            <Select
                                value={marca}
                                onChange={(e) => setMarca(e.target.value)}
                                disabled={loadingData}
                            >
                                {brands.map((b) => (
                                    <MenuItem key={b.id} value={b.nome}>{b.nome}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Categorias (múltiplas) */}
                    <Grid item xs={12} md={8}>
                        <FormControl fullWidth sx={labelStyles}>
                            <InputLabel id="ep-cat-label" shrink>
                                Categorias
                            </InputLabel>
                            <Select
                                labelId="ep-cat-label"
                                id="ep-cat-select"
                                multiple
                                label="Categorias"
                                value={categorias}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setCategorias(typeof v === "string" ? v.split(",") : v);
                                }}
                                displayEmpty
                                renderValue={(selected) =>
                                    !selected || selected.length === 0 ? (
                                        <em style={{ color: "rgba(0,0,0,0.38)" }}>Selecione ao menos uma</em>
                                    ) : (
                                        selected.join(" · ")
                                    )
                                }
                                disabled={loadingData}
                                sx={{ minHeight: 56 }}
                                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
                            >
                                {categories.map((c) => (
                                    <MenuItem key={c.id} value={c.nome}>
                                        {c.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Descrição */}
                    <Grid item xs={12}>
                        <TextField
                            label="Descrição do Produto"
                            fullWidth
                            multiline
                            minRows={4}
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            sx={labelStyles}
                        />
                    </Grid>
                </Grid>



                <Divider sx={{ my: 4 }} />

                {/* ====================== VARIAÇÕES ====================== */}

                <Typography variant="h6" sx={{ mb: 2 }}>
                    Variações / Sabores / Cores
                </Typography>

                {variacoes.map((v, i) => (
                    <Grid container spacing={2} key={i} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={5}>
                            <TextField
                                label="Variação"
                                fullWidth
                                value={v.nome}
                                onChange={(e) => {
                                    const arr = [...variacoes];
                                    arr[i].nome = e.target.value;
                                    setVariacoes(arr);
                                }}
                                sx={labelStyles}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Quantidade"
                                fullWidth
                                value={v.quantidade}
                                onChange={(e) => {
                                    const arr = [...variacoes];
                                    arr[i].quantidade = e.target.value.replace(/\D/g, "");
                                    setVariacoes(arr);
                                }}
                                sx={labelStyles}
                            />
                        </Grid>

                        <Grid item xs={12} md={1} sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton onClick={() => setVariacoes(variacoes.filter((_, idx) => idx !== i))}>
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}

                <Button startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => setVariacoes([...variacoes, { nome: "", quantidade: "" }])}>
                    Adicionar Variação
                </Button>

                <Divider sx={{ my: 4 }} />

                {/* ====================== PREÇOS ====================== */}

                <Typography variant="h6" sx={{ mb: 2 }}>
                    Valores do Produto
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Preço Varejo"
                            fullWidth
                            value={preco}
                            onChange={(e) => setPreco(formatMoney(e.target.value))}
                            sx={labelStyles}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Preço Atacado"
                            fullWidth
                            value={precoDiscount}
                            onChange={(e) => setPrecoDiscount(formatMoney(e.target.value))}
                            sx={labelStyles}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControlLabel
                            control={<Switch checked={isPromo} onChange={(e) => setIsPromo(e.target.checked)} />}
                            label="Promoção?"
                        />
                    </Grid>

                    {isPromo && (
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Preço Promocional (Varejo)"
                                fullWidth
                                value={precoPromo}
                                onChange={(e) => setPrecoPromo(formatMoney(e.target.value))}
                                sx={labelStyles}
                            />
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 4 }} />

                {/* ====================== ESPECIFICAÇÕES ====================== */}

                <Typography variant="h6" sx={{ mb: 2 }}>
                    Especificações
                </Typography>

                {specs.map((s, i) => (
                    <Grid container spacing={2} key={i} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Título"
                                fullWidth
                                value={s.key}
                                onChange={(e) => {
                                    const arr = [...specs];
                                    arr[i].key = e.target.value;
                                    setSpecs(arr);
                                }}
                                sx={labelStyles}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Valor"
                                fullWidth
                                value={s.value}
                                onChange={(e) => {
                                    const arr = [...specs];
                                    arr[i].value = e.target.value;
                                    setSpecs(arr);
                                }}
                                sx={labelStyles}
                            />
                        </Grid>

                        <Grid item xs={12} md={1} sx={{ display: "flex", alignItems: "center" }}>
                            <IconButton onClick={() => setSpecs(specs.filter((_, idx) => idx !== i))}>
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}

                <Button startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => setSpecs([...specs, { key: "", value: "" }])}>
                    Adicionar Especificação
                </Button>

                <Divider sx={{ my: 4 }} />

                {/* ====================== IMAGENS ====================== */}

                <Typography variant="h6" sx={{ mb: 2 }}>
                    Imagens do produto
                </Typography>

                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                    {imagens.map((img) => (
                        <Box key={img.id} sx={{ position: "relative" }}>
                            <img
                                src={img.url}
                                alt=""
                                style={{ width: 100, height: 100, borderRadius: 8, objectFit: "cover" }}
                            />
                            <IconButton
                                size="small"
                                onClick={() => removeImage(img.id)}
                                sx={{ position: "absolute", top: -10, right: -10, bgcolor: "white" }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Stack>

                <input
                    ref={fileInputRef}
                    hidden
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                    onChange={handleImages}
                />

                <Box
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={openFilePicker}
                    sx={{
                        border: "2px dashed",
                        borderColor: dragActive ? "primary.main" : "grey.300",
                        bgcolor: dragActive ? "rgba(28, 100, 242, 0.06)" : "grey.50",
                        borderRadius: 2,
                        py: 4,
                        px: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "border-color 0.2s, background-color 0.2s",
                        "&:hover": {
                            borderColor: "primary.light",
                            bgcolor: "action.hover",
                        },
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                        Arraste imagens aqui ou clique para escolher
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Mesmo fluxo do botão abaixo: upload para o Storage ao salvar o produto.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Formatos: JPG, PNG, WEBP e GIF — até 5 imagens no total
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        openFilePicker();
                    }}
                >
                    Selecionar imagens
                </Button>

            </Paper>

            {/* ====================== POPUP DE ESTOQUE ====================== */}

            <Dialog open={estoqueOpen} onClose={() => setEstoqueOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Estoque das Variações</DialogTitle>

                <DialogContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Variação</TableCell>
                                <TableCell>Quantidade</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {variacoes.map((v, i) => (
                                <TableRow key={i}>
                                    <TableCell>{v.nome}</TableCell>
                                    <TableCell>{v.quantidade}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setEstoqueOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* ====================== BOTÃO FLUTUANTE SALVAR ====================== */}
            <Box
                sx={{
                    position: "fixed",
                    bottom: { xs: 16, md: 24 },
                    right: { xs: 16, md: 32 },
                    zIndex: 1300
                }}
            >
                <Button
                    variant="contained"
                    size="large"
                    onClick={handleSave}
                    disabled={saving || isFormInvalid}
                    sx={{
                        px: 4,
                        py: 1.6,
                        borderRadius: 3,
                        fontWeight: 600,
                        boxShadow: "0 12px 30px rgba(0,0,0,0.25)"

                    }}
                >
                    {saving ? "Salvando..." : "Salvar Produto"}
                </Button>
            </Box>


            {/* ====================== SNACKBAR DE FEEDBACK ====================== */}

            <Snackbar
                open={feedback.open}
                autoHideDuration={1200}
                onClose={() => {
                    setFeedback({ ...feedback, open: false });

                    if (feedback.severity === "success") {
                        navigate("/admin/produtos");
                    }
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setFeedback({ ...feedback, open: false })}
                    severity={feedback.severity}
                    variant="filled"
                    sx={{
                        borderRadius: 2,
                        fontWeight: 500
                    }}
                >
                    {feedback.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
